import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { getDigestPreferences, saveDigestPreferences } from '../../services/emailNotificationService';
import { trackNotificationPreferencesSaved } from '../../services/monEspaceAnalyticsService';

const FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immédiat', description: 'Dès la réception', icon: 'Zap' },
  { value: 'daily', label: 'Quotidien', description: 'Chaque matin', icon: 'Sun' },
  { value: 'weekly', label: 'Hebdomadaire', description: 'Chaque lundi', icon: 'CalendarDays' },
];

const LANGUAGE_OPTIONS = [
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
];

const SMS_TYPES = [
  { key: 'appointment_sms', label: 'Confirmations de rendez-vous', icon: 'CalendarCheck', color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'inquiry_sms', label: 'Demandes de propriété', icon: 'Home', color: 'text-amber-600', bg: 'bg-amber-50' },
  { key: 'notary_sms', label: 'Rendez-vous notaire', icon: 'Scale', color: 'text-purple-600', bg: 'bg-purple-50' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Dimanche' },
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
];

const SectionCard = ({ icon, title, subtitle, children, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth' }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center space-x-3 px-5 py-4 border-b border-border/40">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
        {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const SaveButton = ({ saving, saved, onClick, label = 'Sauvegarder' }) => (
  <div className="flex items-center justify-end space-x-3 pt-2">
    {saved && (
      <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
        <Icon name="CheckCircle" size={13} />
        <span>Sauvegardé</span>
      </span>
    )}
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Sauvegarde…</span>
        </>
      ) : (
        <>
          <Icon name="Save" size={15} color="white" />
          <span>{label}</span>
        </>
      )}
    </button>
  </div>
);

const NotificationPreferences = () => {
  const { user } = useAuth();

  // SMS prefs state
  const [smsPrefs, setSmsPrefs] = useState({
    phone: '',
    sms_enabled: true,
    appointment_sms: true,
    inquiry_sms: true,
    notary_sms: true,
  });
  const [smsLoading, setSmsLoading] = useState(true);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsSaved, setSmsSaved] = useState(false);
  const [smsError, setSmsError] = useState('');

  // Email digest prefs state
  const [digestPrefs, setDigestPrefs] = useState({
    frequency: 'immediate',
    appointmentFrequency: null,
    inquiryFrequency: null,
    notaryFrequency: null,
    preferredHour: 8,
    preferredDayOfWeek: 1,
  });
  const [digestLoading, setDigestLoading] = useState(true);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestSaved, setDigestSaved] = useState(false);

  // Language state
  const [language, setLanguage] = useState('fr');
  const [langSaved, setLangSaved] = useState(false);

  // Load SMS prefs
  const loadSmsPrefs = useCallback(async () => {
    if (!user?.id) { setSmsLoading(false); return; }
    setSmsLoading(true);
    try {
      const { data, error } = await supabase?.from('sms_confirmation_preferences')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();
      if (!error && data) {
        setSmsPrefs({
          phone: data?.phone || '',
          sms_enabled: data?.sms_enabled ?? true,
          appointment_sms: data?.appointment_sms ?? true,
          inquiry_sms: data?.inquiry_sms ?? true,
          notary_sms: data?.notary_sms ?? true,
        });
      }
    } catch (err) {
      console.error('Error loading SMS prefs:', err);
    } finally {
      setSmsLoading(false);
    }
  }, [user?.id]);

  // Load digest prefs
  const loadDigestPrefs = useCallback(async () => {
    if (!user?.id) { setDigestLoading(false); return; }
    setDigestLoading(true);
    const result = await getDigestPreferences(user?.id, user?.email);
    if (result?.success && result?.preferences) {
      setDigestPrefs(result?.preferences);
    }
    setDigestLoading(false);
  }, [user?.id, user?.email]);

  // Load language from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('kapital_language') || 'fr';
    setLanguage(saved);
  }, []);

  useEffect(() => { loadSmsPrefs(); }, [loadSmsPrefs]);
  useEffect(() => { loadDigestPrefs(); }, [loadDigestPrefs]);

  const handleSaveSms = async () => {
    if (!user?.id) return;
    setSmsError('');
    // Basic phone validation
    const phone = smsPrefs?.phone?.trim();
    if (phone && !/^\+?[\d\s\-()]{7,20}$/?.test(phone)) {
      setSmsError('Numéro de téléphone invalide. Format attendu : +225 07 00 00 00 00');
      return;
    }
    setSmsSaving(true);
    try {
      const { error } = await supabase?.from('sms_confirmation_preferences')?.upsert({
          user_id: user?.id,
          phone: phone,
          sms_enabled: smsPrefs?.sms_enabled,
          appointment_sms: smsPrefs?.appointment_sms,
          inquiry_sms: smsPrefs?.inquiry_sms,
          notary_sms: smsPrefs?.notary_sms,
          updated_at: new Date()?.toISOString(),
        }, { onConflict: 'user_id' });
      if (!error) {
        trackNotificationPreferencesSaved({
          frequency: 'sms',
          channels: [
            smsPrefs?.appointment_sms && 'appointment',
            smsPrefs?.inquiry_sms && 'inquiry',
            smsPrefs?.notary_sms && 'notary',
          ]?.filter(Boolean),
        });
        setSmsSaved(true);
        setTimeout(() => setSmsSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving SMS prefs:', err);
    } finally {
      setSmsSaving(false);
    }
  };

  const handleSaveDigest = async () => {
    setDigestSaving(true);
    const result = await saveDigestPreferences(user?.id, user?.email, digestPrefs);
    setDigestSaving(false);
    if (result?.success) {
      trackNotificationPreferencesSaved({
        frequency: digestPrefs?.frequency,
        channels: ['email'],
      });
      setDigestSaved(true);
      setTimeout(() => setDigestSaved(false), 3000);
    }
  };

  const handleSaveLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('kapital_language', lang);
    setLangSaved(true);
    setTimeout(() => setLangSaved(false), 2500);
  };

  const showDeliveryTime = digestPrefs?.frequency === 'daily' || digestPrefs?.frequency === 'weekly' ||
    digestPrefs?.appointmentFrequency === 'daily' || digestPrefs?.appointmentFrequency === 'weekly' ||
    digestPrefs?.inquiryFrequency === 'daily' || digestPrefs?.inquiryFrequency === 'weekly' ||
    digestPrefs?.notaryFrequency === 'daily' || digestPrefs?.notaryFrequency === 'weekly';

  if (!user) {
    return (
      <>
        <div className="min-h-screen bg-surface/30">
          <Header />
          <main className="max-w-2xl mx-auto px-4 py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
              <Icon name="Lock" size={28} className="text-brand-earth" />
            </div>
            <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">
              Connexion requise
            </h2>
            <p className="text-ios-callout text-text-secondary mb-6">
              Connectez-vous pour gérer vos préférences de notifications.
            </p>
            <Link
              to="/homepage"
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors"
            >
              <Icon name="Home" size={16} color="white" />
              <span>Retour à l'accueil</span>
            </Link>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Préférences de Notifications – Kapital States</title>
        <meta name="description" content="Gérez vos préférences de notifications : numéro de téléphone, SMS par type, fréquence des emails et langue." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={14} />
              <Link to="/notification-history" className="hover:text-brand-earth transition-colors">Notifications</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary">Préférences</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth leading-tight">
                  Préférences de Notifications
                </h1>
                <p className="text-ios-callout text-text-secondary mt-1">
                  Centralisez vos paramètres SMS, email et langue en un seul endroit
                </p>
              </div>
              <Link
                to="/notification-history"
                className="flex items-center space-x-1.5 px-3 py-2 rounded-xl text-ios-caption1 text-text-secondary border border-border hover:border-brand-earth/40 hover:text-brand-earth transition-all duration-150 flex-shrink-0"
              >
                <Icon name="History" size={14} />
                <span>Historique</span>
              </Link>
            </div>
          </div>

          <div className="space-y-6">

            {/* ── 1. Phone & SMS ── */}
            <SectionCard
              icon="Smartphone"
              title="Numéro de téléphone & SMS"
              subtitle="Recevez des confirmations par SMS en plus des emails"
              iconBg="bg-green-50"
              iconColor="text-green-600"
            >
              {smsLoading ? (
                <div className="flex items-center space-x-3 py-2">
                  <div className="w-5 h-5 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                  <span className="text-ios-callout text-text-secondary">Chargement…</span>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Phone input */}
                  <div>
                    <label className="block text-ios-footnote font-body-semibold text-text-primary mb-2">
                      Numéro de téléphone
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                        <Icon name="Phone" size={15} className="text-text-secondary" />
                      </div>
                      <input
                        type="tel"
                        value={smsPrefs?.phone}
                        onChange={(e) => { setSmsPrefs((p) => ({ ...p, phone: e?.target?.value })); setSmsError(''); }}
                        placeholder="+225 07 00 00 00 00"
                        className="w-full pl-10 pr-4 py-3 border border-border rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white transition-all"
                      />
                    </div>
                    {smsError && (
                      <p className="mt-1.5 text-ios-caption1 text-red-500 flex items-center space-x-1">
                        <Icon name="AlertCircle" size={12} />
                        <span>{smsError}</span>
                      </p>
                    )}
                    <p className="mt-1.5 text-ios-caption2 text-text-secondary">
                      Format Côte d'Ivoire : +225 07 XX XX XX XX. Laissez vide pour désactiver les SMS.
                    </p>
                  </div>

                  {/* Master SMS toggle */}
                  <div className="flex items-center justify-between p-4 bg-surface rounded-xl border border-border/50">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                        <Icon name="MessageSquare" size={16} className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-ios-callout font-body-semibold text-text-primary">Activer les SMS</p>
                        <p className="text-ios-caption1 text-text-secondary">Canal de secours pour les confirmations</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSmsPrefs((p) => ({ ...p, sms_enabled: !p?.sms_enabled }))}
                      className={`relative w-12 h-6 rounded-full transition-all duration-200 focus:outline-none ${smsPrefs?.sms_enabled ? 'bg-green-500' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${smsPrefs?.sms_enabled ? 'translate-x-6' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  {/* Per-type SMS opt-in */}
                  {smsPrefs?.sms_enabled && (
                    <div>
                      <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">
                        Opt-in par type de notification
                      </p>
                      <div className="space-y-2">
                        {SMS_TYPES?.map((type) => (
                          <div
                            key={type?.key}
                            className="flex items-center justify-between px-4 py-3 bg-surface rounded-xl border border-border/50"
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-7 h-7 rounded-lg ${type?.bg} flex items-center justify-center`}>
                                <Icon name={type?.icon} size={14} className={type?.color} />
                              </div>
                              <span className="text-ios-callout text-text-primary">{type?.label}</span>
                            </div>
                            <button
                              onClick={() => setSmsPrefs((p) => ({ ...p, [type?.key]: !p?.[type?.key] }))}
                              className={`relative w-10 h-5 rounded-full transition-all duration-200 focus:outline-none ${smsPrefs?.[type?.key] ? 'bg-brand-earth' : 'bg-gray-200'}`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${smsPrefs?.[type?.key] ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <SaveButton saving={smsSaving} saved={smsSaved} onClick={handleSaveSms} label="Sauvegarder les paramètres SMS" />
                </div>
              )}
            </SectionCard>

            {/* ── 2. Email Frequency ── */}
            <SectionCard
              icon="Mail"
              title="Fréquence des emails"
              subtitle="Choisissez quand recevoir vos résumés email"
              iconBg="bg-brand-earth/10"
              iconColor="text-brand-earth"
            >
              {digestLoading ? (
                <div className="flex items-center space-x-3 py-2">
                  <div className="w-5 h-5 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
                  <span className="text-ios-callout text-text-secondary">Chargement…</span>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Global frequency */}
                  <div>
                    <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">Fréquence globale</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {FREQUENCY_OPTIONS?.map((opt) => (
                        <button
                          key={opt?.value}
                          onClick={() => setDigestPrefs((p) => ({ ...p, frequency: opt?.value }))}
                          className={`flex flex-col items-start p-4 rounded-xl border transition-all duration-150 text-left ${
                            digestPrefs?.frequency === opt?.value
                              ? 'bg-brand-earth/5 border-brand-earth ring-1 ring-brand-earth/20' :'bg-surface border-border hover:border-brand-earth/40'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${digestPrefs?.frequency === opt?.value ? 'bg-brand-earth/15' : 'bg-white border border-border'}`}>
                            <Icon name={opt?.icon} size={16} className={digestPrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-secondary'} />
                          </div>
                          <p className={`text-ios-callout font-body-semibold ${digestPrefs?.frequency === opt?.value ? 'text-brand-earth' : 'text-text-primary'}`}>{opt?.label}</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">{opt?.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Per-type frequency overrides */}
                  <div>
                    <p className="text-ios-footnote font-body-semibold text-text-primary mb-1">Remplacements par type</p>
                    <p className="text-ios-caption1 text-text-secondary mb-3">Laissez vide pour utiliser la fréquence globale.</p>
                    <div className="space-y-4 bg-surface rounded-xl p-4 border border-border/50">
                      {[
                        { label: 'Confirmations de rendez-vous', icon: 'CalendarCheck', key: 'appointmentFrequency' },
                        { label: 'Demandes de propriété', icon: 'Home', key: 'inquiryFrequency' },
                        { label: 'Rendez-vous notaire', icon: 'Scale', key: 'notaryFrequency' },
                      ]?.map((item, idx) => (
                        <div key={item?.key}>
                          {idx > 0 && <div className="border-t border-border/50 mb-4" />}
                          <div className="flex items-center space-x-1.5 mb-2">
                            <Icon name={item?.icon} size={13} className="text-text-secondary" />
                            <span className="text-ios-caption1 font-body-medium text-text-secondary">{item?.label}</span>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {FREQUENCY_OPTIONS?.map((opt) => (
                              <button
                                key={opt?.value}
                                onClick={() => setDigestPrefs((p) => ({ ...p, [item?.key]: opt?.value === p?.[item?.key] ? null : opt?.value }))}
                                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-ios-caption1 font-system border transition-all duration-150 ${
                                  digestPrefs?.[item?.key] === opt?.value
                                    ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
                                    : 'bg-white text-text-secondary border-border hover:border-brand-earth/40 hover:text-text-primary'
                                }`}
                              >
                                <Icon name={opt?.icon} size={12} />
                                <span>{opt?.label}</span>
                              </button>
                            ))}
                            {digestPrefs?.[item?.key] !== null && digestPrefs?.[item?.key] !== undefined && (
                              <button
                                onClick={() => setDigestPrefs((p) => ({ ...p, [item?.key]: null }))}
                                className="px-2.5 py-1.5 rounded-xl text-ios-caption1 text-text-secondary border border-border hover:border-red-300 hover:text-red-500 transition-all duration-150"
                                title="Utiliser le paramètre global"
                              >
                                <Icon name="RotateCcw" size={12} />
                              </button>
                            )}
                          </div>
                          {(digestPrefs?.[item?.key] === null || digestPrefs?.[item?.key] === undefined) && (
                            <p className="text-ios-caption2 text-text-secondary italic mt-1">Utilise le paramètre global</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delivery time */}
                  {showDeliveryTime && (
                    <div>
                      <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">Heure de livraison préférée</p>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                          <Icon name="Clock" size={15} className="text-text-secondary flex-shrink-0" />
                          <select
                            value={digestPrefs?.preferredHour}
                            onChange={(e) => setDigestPrefs((p) => ({ ...p, preferredHour: parseInt(e?.target?.value) }))}
                            className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>{String(i)?.padStart(2, '0')}:00</option>
                            ))}
                          </select>
                        </div>
                        {(digestPrefs?.frequency === 'weekly') && (
                          <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                            <Icon name="CalendarDays" size={15} className="text-text-secondary flex-shrink-0" />
                            <select
                              value={digestPrefs?.preferredDayOfWeek}
                              onChange={(e) => setDigestPrefs((p) => ({ ...p, preferredDayOfWeek: parseInt(e?.target?.value) }))}
                              className="flex-1 px-3 py-2 border border-border rounded-xl text-ios-footnote font-system focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                            >
                              {DAYS_OF_WEEK?.map((d) => (
                                <option key={d?.value} value={d?.value}>{d?.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <SaveButton saving={digestSaving} saved={digestSaved} onClick={handleSaveDigest} label="Sauvegarder la fréquence" />
                </div>
              )}
            </SectionCard>

            {/* ── 3. Language ── */}
            <SectionCard
              icon="Globe"
              title="Langue des notifications"
              subtitle="Choisissez la langue pour vos emails et SMS"
              iconBg="bg-indigo-50"
              iconColor="text-indigo-600"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  {LANGUAGE_OPTIONS?.map((lang) => (
                    <button
                      key={lang?.value}
                      onClick={() => handleSaveLanguage(lang?.value)}
                      className={`flex items-center space-x-3 p-4 rounded-xl border transition-all duration-150 text-left ${
                        language === lang?.value
                          ? 'bg-indigo-50 border-indigo-400 ring-1 ring-indigo-200' :'bg-surface border-border hover:border-indigo-300'
                      }`}
                    >
                      <span className="text-2xl">{lang?.flag}</span>
                      <div>
                        <p className={`text-ios-callout font-body-semibold ${language === lang?.value ? 'text-indigo-700' : 'text-text-primary'}`}>
                          {lang?.label}
                        </p>
                        {language === lang?.value && (
                          <p className="text-ios-caption1 text-indigo-500 flex items-center space-x-1 mt-0.5">
                            <Icon name="Check" size={11} />
                            <span>Sélectionné</span>
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {langSaved && (
                  <div className="flex items-center space-x-2 text-ios-caption1 text-green-600 bg-green-50 px-3 py-2 rounded-xl border border-green-100">
                    <Icon name="CheckCircle" size={13} />
                    <span>Langue mise à jour</span>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* ── Quick links ── */}
            <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 px-5 py-4">
              <p className="text-ios-footnote font-body-semibold text-text-secondary mb-3 uppercase tracking-wide">Liens rapides</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link
                  to="/notification-history"
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-brand-earth/10 flex items-center justify-center group-hover:bg-brand-earth/15 transition-colors">
                    <Icon name="Bell" size={15} className="text-brand-earth" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-medium text-text-primary">Historique des notifications</p>
                    <p className="text-ios-caption1 text-text-secondary">Voir toutes vos notifications</p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                </Link>
                <Link
                  to="/user-appointment-management"
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Icon name="Calendar" size={15} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-medium text-text-primary">Mes rendez-vous</p>
                    <p className="text-ios-caption1 text-text-secondary">Gérer vos rendez-vous</p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                </Link>
                <Link
                  to="/alert-preferences"
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <Icon name="SlidersHorizontal" size={15} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-medium text-text-primary">Préférences d'alertes</p>
                    <p className="text-ios-caption1 text-text-secondary">Types, canaux et fréquences</p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                </Link>
                <Link
                  to="/alert-preferences"
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                    <Icon name="AlertTriangle" size={15} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="text-ios-callout font-body-medium text-text-primary">Préférences des alertes</p>
                    <p className="text-ios-caption1 text-text-secondary">Gérer vos préférences d'alertes</p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-text-secondary ml-auto" />
                </Link>
              </div>
            </div>

          </div>
        </main>
      </div>
    </>
  );
};

export default NotificationPreferences;
